import { useNavigate } from 'react-router-dom';
import { useToast } from '@chakra-ui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ignoreArticle, unignoreArticle, unsaveArticle } from '../article.api';
import ToastBtnMessage from '../../components/ToastBtnMessage';
import React from 'react';
import DataLayer from '../../utils/data-layer';

export const useIgnoreArticleMutation = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({
      articleId,
      isIgnored,
      toastIgnore
    }: {
      articleId: string;
      isIgnored: boolean;
      toastIgnore: boolean;
    }) => {
      //toastignore is needed only for the undo function. it is normally left undefined, because the isIgnored boolean does not update fast enough
      if (toastIgnore != undefined) {
        if (toastIgnore === true) {
          return unignoreArticle(articleId);
        }
        unsaveArticle(articleId);
        return ignoreArticle(articleId);
      } else {
        if (isIgnored) {
          return unignoreArticle(articleId);
        }
      }
      unsaveArticle(articleId);
      return ignoreArticle(articleId);
    },
    onSuccess: (_, { articleId, isIgnored, toastIgnore }) => {
      let message = isIgnored
        ? `Article restored. You can read it again`
        : `Article ignored. You won't see it again.`;
      let isHiddenButton = isIgnored ? true : false;

      //If the success is from clicking the ignore icon, toastignore is undefined and does nothing
      if (toastIgnore === undefined) {
        toastIgnore = !isIgnored;
      } else {
        //If the success is from clicking undo, use the toastignore value to judge state, then render it undefined
        message = toastIgnore
          ? `Article restored. You can read it again`
          : `Article ignored. You won't see it again.`;
        isHiddenButton = toastIgnore ? true : false;
        toastIgnore = undefined;
      }

      //Only show undo when isIgnored (original state of ignore), is false (meaning you are now ignoring it)
      if (isIgnored === true) {
        toast({
          render: () => (
            <ToastBtnMessage
              title={message}
              isError={false}
              color="jet-black"
              isHiddenButton={isHiddenButton}
            ></ToastBtnMessage>
          ),
          status: 'error',
          duration: 10000,
          isClosable: true,
          position: 'top-right'
        });
      } else if (isIgnored === false) {
        // Track article ignored when transitioning to ignored state (isIgnored=false means we just ignored)
        DataLayer.trackArticleIgnore('general', articleId);
        toast({
          render: () => (
            <ToastBtnMessage
              title={message}
              isError={false}
              isHiddenButton={isHiddenButton}
              color="jet-black"
              btnTitle="Undo"
              btnAction={() => mutation.mutate({ articleId, isIgnored, toastIgnore })}
            ></ToastBtnMessage>
          ),
          status: 'error',
          duration: 10000,
          isClosable: true,
          position: 'top-right'
        });
      }

      // Invalidate the article query to refetch with updated saved status
      queryClient.invalidateQueries({ queryKey: ['article'] });
    },
    onError: (error, { articleId, isIgnored, toastIgnore }) => {
      let isHiddenButton = false;
      const message = isIgnored ? 'Couldn’t restore article!' : 'Couldn’t ignore article!';
      toastIgnore = undefined;
      toast({
        render: () => (
          <ToastBtnMessage
            title={message}
            isError={true}
            isHiddenButton={isHiddenButton}
            color="jet-black"
            btnTitle="Retry"
            btnAction={() => mutation.mutate({ articleId, isIgnored, toastIgnore })}
          />
        ),
        duration: 10000,
        isClosable: true,
        position: 'top-right'
      });
      console.error(error);
    }
  });

  return mutation;
};
